import { useQuery } from "@tanstack/react-query";
import { insuranceOptionGroups } from "@fm/api";
import type { ApiClient } from "@fm/api";
import type { InsuranceOptionGroup } from "@fm/api";
import { getApiBaseUrl } from "../lib/config";

export interface UseInsuranceOptionGroupsOptions {
  apiKey: string;
  fleetId: string;
  apiBaseUrl?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch insurance option groups for a fleet.
 *
 * Groups decide how the options render: a `single` group is a radio set,
 * where picking a tier clears its siblings. Without them every option falls
 * back to an independent checkbox, so the selector needs both lists.
 */
export function useInsuranceOptionGroups(
  options: UseInsuranceOptionGroupsOptions,
) {
  const { apiKey, fleetId, apiBaseUrl, enabled = true } = options;

  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  return useQuery<InsuranceOptionGroup[], Error>({
    queryKey: ["insurance-option-groups", fleetId],
    queryFn: async () => {
      const response = await insuranceOptionGroups.list(apiClient, fleetId);
      return response.data;
    },
    enabled: enabled && !!fleetId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
